import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { useMaintenance } from "@/context/MaintenanceContext";
import { Countdown } from "./Countdown";
import { fmtDateTime, tzAbbr, tzCity, viewerTz } from "@/lib/time";

const Stripes = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07]"
    style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 22px)" }} />
);

export const MaintenanceScreen = ({ status: override, embedded = false }) => {
  const { status: live, refresh } = useMaintenance();
  const { user, setLoginOpen } = useAdmin();
  const navigate = useNavigate();
  const status = override || live || {};
  const tz = viewerTz();
  const ends = status.ends_at;
  const scheduled = status.mode === "scheduled";

  return (
    <div data-testid="maintenance-screen"
      className={`${embedded ? "relative min-h-[560px]" : "relative min-h-screen"} grain flex flex-col bg-[#0A0A0A] text-white`}>
      <Stripes />
      <header className="relative flex items-center justify-between px-5 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-dark.png" alt="Cresway Media logo" className="h-11 w-11 object-contain" />
          <span className="font-display text-lg font-extrabold tracking-tight">Cresway Media</span>
        </div>
        {!embedded && (
          <button onClick={() => (user ? navigate("/dashboard") : setLoginOpen(true))} data-testid="maintenance-login-button"
            className="rounded-full border border-white/30 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white hover:text-black">{user ? "Dashboard" : "Log in"}</button>
        )}
      </header>
      <main className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-5 pb-16 pt-6 sm:px-10">
        <motion.p initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="flex items-center gap-3 font-mono-label text-xs uppercase tracking-[0.25em] text-zinc-300">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E62314] opacity-70" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#E62314]" /></span>
          {scheduled ? "Scheduled maintenance" : "Temporarily offline"}
        </motion.p>
        <h1 data-testid="maintenance-title" className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
          {status.title || "We're making some improvements"}
        </h1>
        {status.message && <p data-testid="maintenance-message" className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg">{status.message}</p>}
        {ends ? (
          <div className="mt-12">
            {status.show_countdown !== false && <Countdown target={ends} onDone={embedded ? undefined : refresh} testId="maintenance-countdown" />}
            <p data-testid="maintenance-back-at" className="mt-6 flex flex-wrap items-center gap-2 text-sm text-zinc-300 sm:text-base">
              <Clock className="h-4 w-4 text-[#E62314]" />
              Back online around <strong className="text-white">{fmtDateTime(ends, tz)}</strong>
              <span className="text-zinc-400">your time · {tzAbbr(ends, tz)} ({tzCity(tz)})</span>
            </p>
          </div>
        ) : (
          <p data-testid="maintenance-back-soon" className="mt-12 text-sm text-zinc-300 sm:text-base">We'll be back as soon as possible. This page refreshes on its own.</p>
        )}
      </main>
      <footer className="relative border-t border-white/10 px-5 py-5 text-xs text-zinc-400 sm:px-10">Copyright 2026 © Cresway Media LLC</footer>
    </div>
  );
};
